// ===================================================================
// Modal: nota da redação e motivos de eliminação (prova com redação)
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { styles } from '../../styles/globalStyles';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';

export const ExamEssayGradeEntryModal = React.memo(({ isOpen, application, onClose, onSaved }) => {
  const { addNotification } = useNotifications();
  const [scoreDraft, setScoreDraft] = useState('');
  const [eliminated, setEliminated] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [saving, setSaving] = useState(false);

  const eligible = Boolean(application?.essay_correction_eligible);

  const essayMax = useMemo(() => {
    const raw = application?.exam_essay_max_score;
    const n = parseInt(String(raw ?? 40), 10);
    if (Number.isNaN(n)) return 40;
    return Math.max(1, Math.min(500, n));
  }, [application]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.getEssayEliminationReasons(false);
        if (!cancelled) setReasons(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setReasons([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !application) return;
    const es = application.exam_essay_score;
    setScoreDraft(es != null && es !== '' ? String(es).replace('.', ',') : '');
    setEliminated(Boolean(application.essay_eliminated));
    const ids = application.essay_elimination_reason_ids;
    setSelectedIds(Array.isArray(ids) ? ids.map((x) => Number(x)).filter((n) => !Number.isNaN(n)) : []);
  }, [isOpen, application]);

  const toggleReason = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!application?.id) return;
    const body = { essay_eliminated: eliminated, essay_elimination_reason_ids: selectedIds };
    if (scoreDraft.trim() !== '') {
      const n = parseFloat(String(scoreDraft).trim().replace(',', '.'));
      if (Number.isNaN(n)) {
        addNotification('Nota inválida', `Informe um número entre 0 e ${essayMax} ou deixe em branco.`, 'warning');
        return;
      }
      if (n < 0 || n > essayMax) {
        addNotification('Nota fora do intervalo', `Use 0 a ${essayMax} (teto do processo).`, 'warning');
        return;
      }
      body.exam_essay_score = n;
    } else {
      body.exam_essay_score = null;
    }
    try {
      setSaving(true);
      const { data, error } = await apiClient.putExamEssayGrades(application.id, body);
      if (error) throw new Error(error);
      addNotification('Redação salva', 'Nota e motivos registrados.', 'success');
      onSaved?.(data);
      onClose();
    } catch (e) {
      addNotification('Erro', e.message || 'Falha ao salvar', 'error');
    } finally {
      setSaving(false);
    }
  }, [application, scoreDraft, eliminated, selectedIds, essayMax, addNotification, onSaved, onClose]);

  if (!application) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Redação — #${application.id}`} large>
      <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
        {application.candidate_name} · {application.course_name || 'Curso'}
      </p>
      {!eligible && (
        <p style={{ fontSize: '13px', color: '#B45309', background: '#FFFBEB', padding: 10, borderRadius: 8 }}>
          Esta inscrição <strong>não está no corte</strong> para correção da redação (multiplicador × vagas). Como
          administrador você ainda pode lançar; o analista só consegue se estiver elegível.
        </p>
      )}
      <div style={{ ...styles.formRow, marginTop: 12 }}>
        <label style={{ ...styles.label, minWidth: '160px' }}>{`Nota redação (0–${essayMax})`}</label>
        <input
          type="text"
          inputMode="decimal"
          value={scoreDraft}
          onChange={(e) => setScoreDraft(e.target.value)}
          placeholder="vazio = não informada"
          style={{ ...styles.input, maxWidth: '120px' }}
        />
      </div>
      <div style={{ ...styles.formRow, alignItems: 'center' }}>
        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={eliminated} onChange={(e) => setEliminated(e.target.checked)} />
          Eliminado na redação
        </label>
      </div>
      <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: 8 }}>Motivos de eliminação (marque um ou mais):</p>
      <div
        style={{
          maxHeight: 220,
          overflowY: 'auto',
          border: '1px solid #E5E7EB',
          borderRadius: 8,
          padding: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {reasons.length === 0 ? (
          <span style={{ color: '#9CA3AF', fontSize: '13px' }}>Nenhum motivo ativo. Cadastre em Configuração → Motivos redação.</span>
        ) : (
          reasons.map((r) => (
            <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleReason(r.id)} />
              <span>
                <strong>{r.title}</strong>
                {r.description ? <span style={{ color: '#6B7280' }}> — {r.description}</span> : null}
              </span>
            </label>
          ))
        )}
      </div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
        <button type="button" onClick={onClose} style={{ ...styles.button, ...styles.buttonSecondary }} disabled={saving}>
          Cancelar
        </button>
        <button type="button" onClick={handleSubmit} style={{ ...styles.button, ...styles.buttonPrimary }} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </Modal>
  );
});
